/**
 * streamingMarkdown.js — utilities för att rendera markdown SÄKERT under en
 * pågående text-stream. Kärnan är `safeStreamingMarkdown(text)` som skär bort
 * halv-rendererade tabellblock så att ReactMarkdown inte producerar trasiga
 * `<table>`-element under tiden tokens trillar in.
 *
 * Spec: kanai-status-component-spec.md (v2) — sektion "Markdown-tabellbuffring".
 */

/**
 * Är raden en markdown-tabellrad? Börjar med valfri whitespace + `|`.
 * @param {string} line
 */
function isTableRow(line) {
  return /^\s*\|/.test(line);
}

/**
 * Är raden en separator-rad i en markdown-tabell? T.ex. `|---|---|` eller
 * `| :--- | ---: |`.
 * @param {string} line
 */
function isTableSeparator(line) {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
}

/**
 * En tabellrad är "komplett" när den slutar med `|` (vilket den ska göra
 * efter att alla celler är skrivna). Halvskrivna rader saknar avslutande `|`.
 * @param {string} line
 */
function isCompleteTableRow(line) {
  const t = line.trimEnd();
  return t.endsWith('|');
}

/**
 * Rensa markdown-text inför rendering under streaming.
 *
 * Regler:
 *  1) Om sista raden är en HALV tabellrad (börjar med `|` men slutar inte
 *     med `|`) — trunca den raden. ReactMarkdown ska inte få halv-tabellen.
 *  2) Om vi har en tabell-header MEN saknar separator-rad (`|---|---|`) ännu
 *     — dölj header-raden också (annars renderas en konstig 1-radig tabell).
 *  3) Om sista raden är `>` (block-quote utan innehåll) — låt den vara,
 *     ReactMarkdown hanterar det bra.
 *
 * Returnerar texten utan trailing newline-quirks.
 *
 * @param {string} text - rå streamad markdown-text
 * @returns {string} - säker att ge till ReactMarkdown
 */
export function safeStreamingMarkdown(text) {
  if (!text || typeof text !== 'string') return text || '';

  // Snabb-väg: ingen tabell alls
  if (text.indexOf('|') === -1) return text;

  const lines = text.split('\n');
  if (lines.length === 0) return text;

  // 1) Trunca halvskriven tabellrad i slutet
  let last = lines[lines.length - 1];
  if (isTableRow(last) && !isCompleteTableRow(last)) {
    lines.pop();
  }

  if (lines.length === 0) return '';

  // 2) Dölj tabell-header utan separator. Vi söker bakifrån efter ett
  //    "potentiellt aktivt" tabellblock och kollar att det har separator.
  //    Letar efter den närmsta starten på en tabellrun i slutet.
  let i = lines.length - 1;
  // hoppa förbi non-table-rader längst ner (t.ex. tom rad)
  while (i >= 0 && !isTableRow(lines[i]) && lines[i].trim() === '') i--;
  // Om vi hittade en tabellrad i slutet — gå uppåt och hitta starten
  if (i >= 0 && isTableRow(lines[i])) {
    let endIdx = i;
    let startIdx = i;
    while (startIdx - 1 >= 0 && isTableRow(lines[startIdx - 1])) {
      startIdx--;
    }
    // Tabellblock är lines[startIdx..endIdx]
    const block = lines.slice(startIdx, endIdx + 1);
    // En giltig tabell kräver minst 2 rader: header + separator
    const hasSeparator = block.some(isTableSeparator);
    if (!hasSeparator) {
      // Ofullständig tabell — klipp bort den helt
      return lines.slice(0, startIdx).join('\n').replace(/\n+$/, '');
    }
  }

  return lines.join('\n');
}

export default safeStreamingMarkdown;
